import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { IonicModule, ModalController, PopoverController } from '@ionic/angular';
import { ColumnMode, NgxDatatableModule, SelectionType } from '@swimlane/ngx-datatable';
import moment from 'moment';
import { NgxTippyModule } from 'ngx-tippy-wrapper';
import { BehaviorSubject } from 'rxjs';

import {
  ActionDropdownComponent,
  ActionDropdownType,
} from 'src/app/components/action-dropdown/action-dropdown.component';
import { AddMemberPage } from 'src/app/components/add-member/add-member.page';
import { AvatarModule } from 'src/app/components/avatar/avatar.module';
import { FilterDropdownComponent } from 'src/app/components/filter-dropdown/filter-dropdown.component';
import { MergeUsersModalComponent } from 'src/app/components/merge-users-modal/merge-users-modal.component';
import { MergeUsersModalModule } from 'src/app/components/merge-users-modal/merge-users-modal.module';
import { USER_STATUS_ACTIVE, USER_STATUS_INACTIVE } from 'src/app/constants';
import {
  downloadCSV,
  flattenAdditionalIdpFields,
  getAdditionalIdpFieldId,
  getCompanyCustomFieldId,
  getCustomFieldId,
} from 'src/app/utils/utils';
import { Profile, WhereClause } from 'src/models';
import { TENANT_USER_ROLES, TenantUserRole } from 'src/models/users';
import { SharedPipesModule } from 'src/pipes/shared-pipes.module';
import { AuthService } from 'src/services/auth.service';
import { MessageService } from 'src/services/message.service';
import { SlackService } from 'src/services/slack.service';
import { UsersService } from 'src/services/users.service';

const FIELD_TYPE_SELECT = 'select';

@Component({
  standalone: true,
  imports: [
    RouterModule,
    CommonModule,
    FormsModule,
    IonicModule,
    NgxDatatableModule,
    AvatarModule,
    MergeUsersModalModule,
    NgxTippyModule,
    SharedPipesModule,
  ],
  selector: 'app-admin-people',
  templateUrl: './admin-people.component.html',
  styleUrls: ['./admin-people.component.scss'],
})
export class AdminPeopleComponent implements OnInit {
  private allUsers: Partial<Profile>[] = [];
  public readonly users$: BehaviorSubject<Partial<Profile>[]> = new BehaviorSubject([]);
  public isLoading = true;
  public showInactiveUsers = false;
  public customFields = [];
  public searchText: string;
  public columns = [];
  public filters = [];
  public selectedColumnIds: string[] = [];
  public selectedUserIds: string[] = [];
  public tagId?: string;
  public showTeamOptionsModal = false;
  public isUserAutoAssignableDefault: boolean;
  public isLoadingTeamOptions = false;
  connectedTeams: any[] = [];
  selectedTeamId?: string;
  selectedRole: string;

  ColumnMode = ColumnMode;
  SelectionType = SelectionType;
  TENANT_USER_ROLES = TENANT_USER_ROLES;
  accountsModeEnabled = false;

  constructor(
    private usersSrvc: UsersService,
    private popoverCtrl: PopoverController,
    private modalCtrl: ModalController,
    public authService: AuthService,
    private msgService: MessageService,
    private usersService: UsersService,
    private slackService: SlackService,
  ) {}

  async ngOnInit() {
    this.connectedTeams = await this.slackService.getConnectedTeams();
    // this.selectedTeamId = this.connectedTeams?.find((team) => team.isPrimary)?.id;
    this.accountsModeEnabled = this.authService.tenant?.accountsEnabled ?? false;

    this.isUserAutoAssignableDefault = this.authService.tenant?.isUserAutoAssignableDefault ?? true;

    await this.loadUsers();

    this.setColumns();
    this.isLoading = false;
  }

  setNextUsers(users: any[]) {
    this.users$.next(
      users.filter((u) => {
        if (this.showInactiveUsers) {
          return true;
        }

        // NOTE: Can't check status === 'active' because not all users have status set right now.
        return u.status !== USER_STATUS_INACTIVE && u.deleted !== true;
      }),
    );
  }

  async fetchUsers() {
    const where: WhereClause[] = [];

    if (this.selectedRole) {
      where.push({
        field: 'role',
        operator: '==',
        value: this.selectedRole,
      });
    }

    this.isLoading = true;

    const users = await this.usersSrvc.list({
      limit: null,
      order: ['name'],
      descending: false,
      where,
      showInactiveUsers: this.showInactiveUsers,
      internalUsersSlackTeamId: this.selectedTeamId,
    });

    this.allUsers = users.data.map((u) => ({
      id: u.id,
      email: u.email,
      photo: u.photo,
      name: u.name,
      phoneNumber: u.phoneNumber,
      pronoun: u.pronoun,
      title: u.title,
      department: u.department,
      organization: u.organization,
      division: u.division,
      costCenter: u.costCenter,
      employeeNumber: u.employeeNumber,
      streetAddress: u.streetAddress,
      locality: u.locality,
      region: u.region,
      postalCode: u.postalCode,
      country: u.country,
      createdAt: u.createdAt,
      lastSeenAt: u.lastSeenAt,
      status: u.status,
      deleted: u.deleted,
      ...Object.entries(flattenAdditionalIdpFields(u.additionalIdpFields ?? {})).reduce(
        (acc, [key, value]) => ({ ...acc, [getAdditionalIdpFieldId(key)]: value }),
        {},
      ),
      ...(u.customFields ?? []).reduce((acc, f) => ({ ...acc, [getCustomFieldId(f)]: f.value }), {}),
      role: u.role,
      awayUntil: u.awayUntil,
      backupUserId: u.backupUserId,
      isAutoAssignable: u.isAutoAssignable,
      invitedAt: u.invitedAt,
    }));

    this.isLoading = false;

    return this.allUsers;
  }

  async loadUsers() {
    const users = await this.fetchUsers();

    this.setNextUsers(users);
  }

  async setColumns() {
    const columns: any[] = [
      {
        label: 'Name',
        id: 'name',
        selected: true,
        minWidth: 320,
        filter: {
          type: 'text',
        },
      },
      {
        label: 'Email',
        id: 'email',
        selected: true,
        minWidth: 320,
        filter: {
          type: 'text',
        },
      },
      {
        label: 'Phone',
        id: 'phoneNumber',
        selected: false,
        minWidth: 200,
        filter: {
          type: 'text',
        },
      },
      {
        label: 'Role',
        id: 'role',
        selected: true,
        minWidth: 100,
      },
      {
        label: 'Status',
        id: 'status',
        selected: true,
        minWidth: 100,
      },
    ];

    if (!this.accountsModeEnabled) {
      columns.push({
        label: 'Can Respond to Tickets',
        id: 'isAutoAssignable',
        selected: true,
        minWidth: 150,
      });
    }

    columns.push(
      {
        label: 'Joined',
        id: 'createdAt',
        selected: true,
        minWidth: 225,
      },
      {
        label: 'Last Seen',
        id: 'lastSeenAt',
        selected: true,
        minWidth: 225,
      },
      ...(this.authService.tenant?.users?.customProfileFields ?? []).map((f) => ({
        label: f.label ?? f.title,
        id: getCompanyCustomFieldId(f),
        selected: true,
        minWidth: 350,
        filter: {
          type: f.options ? 'select' : 'text',
          options: f.options?.map((o) => o.label),
        },
      })),
    );

    this.columns = columns;

    this.updateSelectedColumnIds();
  }

  async changeColumns() {
    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      componentProps: {
        options: this.columns.map((c) => ({
          label: c.label,
          id: c.id,
          selected: c.selected,
        })),
        checkbox: true,
        multiCheckbox: true,
        callback: (_i, _label, id) => {
          const column = this.columns.find((c) => c.id === id);

          column.selected = !column.selected;

          this.columns = [...this.columns];
          this.updateSelectedColumnIds();
        },
        onCloseCallback() {
          popover.dismiss();
        },
      },
      showBackdrop: false,
      backdropDismiss: true,
      keyboardClose: true,
      event,
    });

    popover.present();
  }

  async updateFilteredUsers() {
    this.setColumns();

    const users = await this.fetchUsers();

    this.setNextUsers(
      users.filter((u) =>
        this.filters.every((f) => {
          if (!f?.value) {
            return true;
          }

          if (
            String(u[f.id] ?? '')
              .toLowerCase()
              .trim()
              .includes(f.value.toLowerCase().trim())
          ) {
            return true;
          }

          return false;
        }),
      ),
    );
  }

  addFilter(column, value) {
    this.filters = [...this.filters, { label: column.label, id: column.id, value }];
    this.updateFilteredUsers();
  }

  removeFilter(i) {
    this.filters = [...this.filters.slice(0, i), ...this.filters.slice(i + 1)];
    this.updateFilteredUsers();
  }

  changeFilters() {
    const _handler = async (e) => {
      const popover = await this.popoverCtrl.create({
        component: ActionDropdownComponent,
        componentProps: {
          options: this.columns.filter((c) => c.filter),
          checkbox: false,
          multiCheckbox: false,
          callback: async (_i, _label, id) => {
            popover.dismiss();

            const column = this.columns.find((c) => c.id === id);
            let fieldValues: string[] | undefined = column.filter.options;
            let fieldType: string = column.filter.type;

            if (fieldType !== FIELD_TYPE_SELECT) {
              fieldValues = (await this.usersSrvc.getFieldValues(column.id)) as string[];

              if (fieldValues?.length) {
                fieldType = FIELD_TYPE_SELECT;
              }
            }

            const innerPopover = await this.popoverCtrl.create({
              component: FilterDropdownComponent,
              componentProps: {
                label: column.label,
                type: fieldType,
                options: fieldValues,
                onApplyCallback: (value) => {
                  this.addFilter(column, value);

                  innerPopover.dismiss();
                },
                onBackCallback: () => {
                  innerPopover.dismiss();
                  _handler(e);
                },
              },
              showBackdrop: true,
              backdropDismiss: true,
              keyboardClose: true,
              event: e,
            });

            innerPopover.present();
          },
        },
        showBackdrop: true,
        backdropDismiss: true,
        keyboardClose: true,
        event: e,
      });

      popover.present();
    };

    _handler(event);
  }

  updateSelectedColumnIds() {
    this.selectedColumnIds = this.getSelectedColumns().map((c) => c.id);
  }

  getSelectedColumns() {
    return this.columns.filter((c) => c.selected);
  }

  getSelectedUsers() {
    const users = this.users$.getValue();

    if (!this.selectedUserIds.length) {
      return users;
    }

    return users.filter((u) => this.selectedUserIds.includes(u.id));
  }

  downloadUsers() {
    downloadCSV(this.getSelectedUsers(), [...this.getSelectedColumns(), 'id'], `people-${this.authService.tenantId}`);
  }

  onSelect({ selected }) {
    this.selectedUserIds.splice(0, this.selectedUserIds.length);
    this.selectedUserIds.push(...selected.map((u) => u.id));
  }

  getAwayStatus(user: Profile) {
    if (user.status === 'inactive') {
      return 'Deactivated';
    }
    if (!user.awayUntil || moment(user.awayUntil).isBefore(moment())) {
      return 'Active';
    }
    return `Away until ${moment(user.awayUntil).format('MMM d')}`;
  }

  async addMember() {
    const modal = await this.modalCtrl.create({
      component: AddMemberPage,
      cssClass: 'modal-content-only',
    });

    modal.present();

    modal.onWillDismiss().then(() => {
      return this.loadUsers();
    });
  }

  async saveTeamOptions() {
    this.isLoadingTeamOptions = true;

    const updates: any = {
      isUserAutoAssignableDefault: this.isUserAutoAssignableDefault,
    };

    try {
      await this.authService.updateTenant(updates);

      this.showTeamOptionsModal = false;

      this.msgService.showBottom('✔︎ Saved');
    } catch (err) {
      this.msgService.showBottom(`Whoops! An error occurred: ${err.message}`);
    } finally {
      this.isLoadingTeamOptions = false;
    }
  }

  getIsAutoAssignableBadgeColor(user: any) {
    const isUserAutoAssignableDefault = this.authService.tenant?.isUserAutoAssignableDefault;

    if (user.isAutoAssignable === null) {
      return isUserAutoAssignableDefault ? 'success' : 'warning';
    }

    return user.isAutoAssignable ? 'success' : 'warning';
  }

  getIsAutoAssignableLabel(user: any) {
    const isUserAutoAssignableDefault = this.authService.tenant?.isUserAutoAssignableDefault;

    if (user.isAutoAssignable === null) {
      return `Default (${isUserAutoAssignableDefault ? 'Yes' : 'No'})`;
    }

    return user.isAutoAssignable ? 'Yes' : 'No';
  }

  async showDeactivateOption(user: any) {
    const isActive = user.status === USER_STATUS_ACTIVE;
    const options: ActionDropdownType[] = [
      {
        label: isActive ? 'Deactivate user' : 'Reactivate user',
        icon: isActive ? 'close-circle-outline' : 'add-circle-outline',
      },
    ];

    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      componentProps: {
        options,
        callback: async (_index: number, _label: string) => {
          popover.dismiss();

          // Deactivate user
          const newStatus = isActive ? USER_STATUS_INACTIVE : USER_STATUS_ACTIVE;
          await this.usersService.updateUser(user.id, {
            status: newStatus,
          });
          user.status = newStatus;
        },
      },
      showBackdrop: false,
      event,
    });
    popover.present();
  }

  async changeAutoAssignableSetting(user: any) {
    const options: ActionDropdownType[] = [
      {
        label: 'Use default',
      },
      {
        label: 'No',
      },
      {
        label: 'Yes',
      },
    ];
    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      componentProps: {
        options,
        callback: async (i) => {
          popover.dismiss();

          let isAutoAssignable = null;
          if (i === 1) {
            isAutoAssignable = false;
          } else if (i === 2) {
            isAutoAssignable = true;
          }

          await this.usersSrvc.updateUser(user.id, { isAutoAssignable });
          this.loadUsers();
        },
        onCloseCallback() {
          popover.dismiss();
        },
      },
      showBackdrop: true,
      backdropDismiss: true,
      keyboardClose: true,
      event,
    });

    popover.present();
  }

  async changeRole(userId: string) {
    const options: ActionDropdownType[] = [
      this.authService.isTenantOwner
        ? {
            label: 'Owner',
            id: TENANT_USER_ROLES.OWNER,
            icon: 'shield-checkmark-outline',
          }
        : undefined,
      {
        label: 'Admin',
        id: TENANT_USER_ROLES.ADMIN,
        icon: 'shield-outline',
      },
      {
        label: 'Standard',
        id: TENANT_USER_ROLES.STANDARD,
      },
    ].filter(Boolean);
    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      componentProps: {
        options,
        callback: async (i) => {
          popover.dismiss();

          const role = options[i].id as TenantUserRole;

          await this.usersService.updateUser(userId, { role });

          this.loadUsers();
        },
        onCloseCallback() {
          popover.dismiss();
        },
      },
      showBackdrop: true,
      backdropDismiss: true,
      keyboardClose: true,
      event,
    });

    popover.present();
  }

  async inviteUser() {
    const modal = await this.modalCtrl.create({
      component: AddMemberPage,
      componentProps: {
        welcomeEmailEnabled: false,
      },
      cssClass: 'modal-content-only',
    });

    modal.present();

    await modal.onWillDismiss();

    this.loadUsers();
  }

  async mergeUsers() {
    const modal = await this.modalCtrl.create({
      component: MergeUsersModalComponent,
    });

    modal.present();

    const result = await modal.onWillDismiss();

    if (result.data?.merged) {
      await this.loadUsers();
    }
  }
}
